import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MdClose, MdDownload } from 'react-icons/md';

const PROD_RESUME_URL = 'https://suhasp.dev/resume.pdf';

const getViewerSrc = () => {
  const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  if (isLocal) return '/resume.pdf';
  return `https://docs.google.com/viewer?url=${encodeURIComponent(PROD_RESUME_URL)}&embedded=true`;
};

const ResumeModal = ({ isOpen, onClose }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          onClick={onClose}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="relative w-full max-w-4xl h-[90vh] bg-darkSurface rounded-xl border border-white/10 shadow-2xl flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 flex-shrink-0">
              <span className="text-textPrimary font-medium">Resume</span>
              <div className="flex items-center gap-2">
                <a
                  href={PROD_RESUME_URL}
                  download="Suhas_P_Resume.pdf"
                  className="flex items-center gap-2 px-4 py-1.5 bg-primary hover:bg-primaryDark text-white rounded-lg text-sm font-medium transition-all"
                >
                  <MdDownload className="w-4 h-4" />
                  Download
                </a>
                <button
                  onClick={onClose}
                  className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center transition-all"
                  aria-label="Close"
                >
                  <MdClose className="w-4 h-4 text-textSecondary" />
                </button>
              </div>
            </div>

            {/* PDF Viewer - Google Docs viewer in production, raw PDF on localhost */}
            <iframe
              key={isOpen ? 'open' : 'closed'}
              src={getViewerSrc()}
              title="Resume"
              sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
              className="flex-1 w-full border-0 bg-white"
            />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ResumeModal;
